import React, { useState, useEffect } from "react";
import { StyleSheet, View, ScrollView, Text } from "react-native";
import { colors } from "src/assets";
import {
  TabControl,
  NotFound,
  ChatHistoryItem,
  ChatHistoryValue,
} from "./components";
import { usePreOfferHistory, useCognito } from "src/hooks";
import { useNavigation } from "@react-navigation/native";
import { tabChangeEmit } from "src/utils/event";
import { IPreConversation } from "src/domains";
import LocalText from "src/local";
import { useRecoilValue } from "recoil";
import { MainTabScreenProps } from "src/navigation/types";

enum PreOfferChatListTab {
  All = 0,
  Sent = 1,
  Received = 2,
}

export const PreOfferChatList: React.FC = () => {
  const navigation =
    useNavigation<MainTabScreenProps<"PreOfferChatList">["navigation"]>();
  const [tab, setTab] = useState<PreOfferChatListTab>(PreOfferChatListTab.All);
  const [cognitoUsername] = useCognito();
  const [chats, chatsLoading] = usePreOfferHistory();
  const [chatsAll, setChatsAll] = useState<Array<ChatHistoryValue>>([]);
  const [chatsSent, setChatsSent] = useState<Array<ChatHistoryValue>>([]);
  const [chatsReceived, setChatsReceived] = useState<Array<ChatHistoryValue>>(
    []
  );
  useEffect(() => {
    setChatsAll(
      chats
        .map((item) => ({
          ...item,
          lastDate:
            // item.lastReceivedMessage?.createdAt ||
            item.lastMessage?.createdAt || item.updatedAt,
        }))
        .sort((a, b) => {
          if (
            new Date(a.lastDate || "1970-01-01").getTime() <
            new Date(b.lastDate || "1970-01-01").getTime()
          ) {
            return 1;
          } else if (
            new Date(a.lastDate || "1970-01-01").getTime() >
            new Date(b.lastDate || "1970-01-01").getTime()
          ) {
            return -1;
          }
          return 0;
        })
    );
    if (cognitoUsername) {
      setChatsSent(
        chats
          .filter((item) => item.senderId === cognitoUsername)
          .map((item) => ({
            ...item,
            lastDate:
              // item.lastReceivedMessage?.createdAt ||
              item.lastMessage?.createdAt || item.updatedAt,
          }))
          .sort((a, b) => {
            if (
              new Date(a.lastDate || "1970-01-01").getTime() <
              new Date(b.lastDate || "1970-01-01").getTime()
            ) {
              return 1;
            } else if (
              new Date(a.lastDate || "1970-01-01").getTime() >
              new Date(b.lastDate || "1970-01-01").getTime()
            ) {
              return -1;
            }
            return 0;
          })
      );
      setChatsReceived(
        chats
          .filter((item) => item.opponentId === cognitoUsername)
          .map((item) => ({
            ...item,
            lastDate:
              // item.lastReceivedMessage?.createdAt ||
              item.lastMessage?.createdAt || item.updatedAt,
          }))
          .sort((a, b) => {
            if (
              new Date(a.lastDate || "1970-01-01").getTime() <
              new Date(b.lastDate || "1970-01-01").getTime()
            ) {
              return 1;
            } else if (
              new Date(a.lastDate || "1970-01-01").getTime() >
              new Date(b.lastDate || "1970-01-01").getTime()
            ) {
              return -1;
            }
            return 0;
          })
      );
    }
  }, [chats, cognitoUsername]);
  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", (e) => {
      tabChangeEmit("PreOfferChatList");
    });
    return () => {
      unsubscribe();
    };
  }, [navigation]);
  return (
    <View style={styles.container}>
      <TabControl
        tabs={[
          { label: LocalText.screens.preOfferChatList.tabAll },
          { label: LocalText.screens.preOfferChatList.tabSent },
          { label: LocalText.screens.preOfferChatList.tabReceived },
        ]}
        selectedIndex={tab}
        onChange={(index) => setTab(index)}
      />
      {tab === PreOfferChatListTab.All ? (
        <View style={{ flex: 1 }}>
          {!chatsLoading && chats.length === 0 ? (
            <NotFound text={LocalText.screens.preOfferChatList.notFound} />
          ) : null}
          {chats.length > 0 && cognitoUsername ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {chatsAll.map((item, index) => (
                <ChatHistoryItem
                  key={index}
                  color={index % 2 === 0 ? "white" : "transparent"}
                  cognitoUserId={cognitoUsername}
                  item={item}
                  onPress={() =>
                    navigation.push("PreOfferChat", { preOfferId: item.id })
                  }
                />
              ))}
            </ScrollView>
          ) : null}
        </View>
      ) : null}
      {tab === PreOfferChatListTab.Sent ? (
        <>
          {!chatsLoading && chatsSent.length === 0 ? (
            <NotFound text={LocalText.screens.preOfferChatList.notFound} />
          ) : null}
          {chatsSent.length > 0 && cognitoUsername ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {chatsSent.map((item, index) => (
                <ChatHistoryItem
                  key={index}
                  color={index % 2 === 0 ? "white" : "transparent"}
                  cognitoUserId={cognitoUsername}
                  item={item}
                  onPress={() =>
                    navigation.push("PreOfferChat", { preOfferId: item.id })
                  }
                />
              ))}
            </ScrollView>
          ) : null}
        </>
      ) : null}
      {tab === PreOfferChatListTab.Received ? (
        <>
          {!chatsLoading && chatsReceived.length === 0 ? (
            <NotFound text={LocalText.screens.preOfferChatList.notFound} />
          ) : null}
          {chatsReceived.length > 0 && cognitoUsername ? (
            <ScrollView showsVerticalScrollIndicator={false}>
              {chatsReceived.map((item, index) => (
                <ChatHistoryItem
                  key={index}
                  color={index % 2 === 0 ? "white" : "gray"}
                  cognitoUserId={cognitoUsername}
                  item={item}
                  onPress={() =>
                    navigation.push("PreOfferChat", { preOfferId: item.id })
                  }
                />
              ))}
            </ScrollView>
          ) : null}
        </>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
});
